import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconIdBadge2,
  IconMail,
  IconMailForward,
  IconShieldLock,
  IconUser,
} from "@tabler/icons-react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  Input,
  Paper,
  PageHeader,
  Skeleton,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { useAuthStore } from "@/lib/auth-store";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { forgotPassword } from "@/shared/api/auth.api";
import { SeccionDosFactores } from "@/features/seguridad/components/SeguridadPage";
import { miPerfilQueryOptions, misAsignacionesQueryOptions } from "../hooks/use-perfil-queries";
import { actualizarMiPerfil } from "../services/perfil.api";

/**
 * Mi perfil (P40) · el usuario autenticado ve y configura SU cuenta en dos
 * pestañas: Perfil (nombre y correo editables + roles/áreas/organizaciones
 * de solo lectura) y Seguridad (2FA · reutiliza la sección de la página
 * Seguridad).
 */
export function PerfilPage() {
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6 md:px-6">
      <PageHeader
        title="Mi perfil"
        description="Consulta y configura los datos de tu cuenta."
      />
      <Tabs defaultValue="perfil">
        <TabsList className="mb-4">
          <TabsTrigger value="perfil" className="flex items-center gap-1.5">
            <IconUser size={15} stroke={1.7} />
            Perfil
          </TabsTrigger>
          <TabsTrigger value="seguridad" className="flex items-center gap-1.5">
            <IconShieldLock size={15} stroke={1.7} />
            Seguridad
          </TabsTrigger>
        </TabsList>

        <TabsContent value="perfil">
          <TabPerfil />
        </TabsContent>
        <TabsContent value="seguridad">
          <Stack gap="md">
            <CardRestablecerPassword />
            <SeccionDosFactores />
          </Stack>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TabPerfil() {
  const qc = useQueryClient();
  const updateUser = useAuthStore((s) => s.updateUser);
  const perfil = useQuery(miPerfilQueryOptions);
  const asignaciones = useQuery(misAsignacionesQueryOptions);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  // Precarga del form cuando llega el perfil (y tras guardar).
  useEffect(() => {
    if (perfil.data) {
      setFullName(perfil.data.fullName);
      setEmail(perfil.data.email);
    }
  }, [perfil.data]);

  const guardar = useMutation({
    mutationFn: () => actualizarMiPerfil({ fullName: fullName.trim(), email: email.trim() }),
    onSuccess: (data) => {
      qc.setQueryData(qk.miPerfil, data);
      // La sesión refleja el cambio al instante (header/menú de usuario).
      updateUser({ fullName: data.fullName, email: data.email });
      toastExito({ title: "Perfil actualizado", message: "Tus datos se guardaron." });
    },
    onError: toastErrorApi,
  });

  // Skeleton mientras llega el perfil (en vez de `return null`, que dejaba
  // la pestaña en blanco hasta que respondía el servidor).
  if (!perfil.data) return <TabPerfilSkeleton />;
  const p = perfil.data;
  const sinCambios = fullName.trim() === p.fullName && email.trim() === p.email;
  const iniciales = (p.fullName || p.email)
    .split(/[\s@.]+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <Stack gap="md">
      <Paper padding="lg">
        <Group gap="md" wrap="nowrap">
          <Avatar size="lg" radius="md">
            {iniciales}
          </Avatar>
          <div className="min-w-0">
            <Text fw={600} truncate>
              {p.fullName || p.email}
            </Text>
            <Text size="sm" c="dimmed" truncate>
              {p.email}
            </Text>
            <Group gap={4} className="mt-1.5">
              {p.roles.map((r) => (
                <Badge key={r} size="xs">
                  {r}
                </Badge>
              ))}
            </Group>
          </div>
        </Group>
      </Paper>

      <Paper padding="lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar.mutate();
          }}
        >
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Datos de la cuenta
            </Text>
            <Input
              label="Nombre completo"
              leftSection={<IconIdBadge2 size={16} stroke={1.6} />}
              value={fullName}
              onChange={(e) => setFullName(e.currentTarget.value)}
              required
              maxLength={200}
            />
            <Input
              label="Correo electrónico"
              description="Es también tu usuario para iniciar sesión."
              leftSection={<IconMail size={16} stroke={1.6} />}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
              maxLength={256}
            />
            <Group justify="flex-end">
              <Button
                type="submit"
                disabled={sinCambios || !fullName.trim() || !email.trim()}
                loading={guardar.isPending}
              >
                Guardar cambios
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>

      <Paper padding="lg">
        <Stack gap="sm">
          <div>
            <Text fw={600} size="sm">
              Asignaciones
            </Text>
            <Text size="xs" c="dimmed">
              Las gestiona el administrador · si necesitas un cambio, contáctalo.
            </Text>
          </div>
          <Divider />
          <div>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" className="mb-1">
              Organizaciones
            </Text>
            <Group gap={4}>
              {(asignaciones.data?.organizaciones ?? []).length === 0 ? (
                <Text size="sm" c="dimmed">
                  Sin organizaciones asignadas.
                </Text>
              ) : (
                asignaciones.data!.organizaciones.map((o) => (
                  <Badge key={o} size="sm" color="info">
                    {o}
                  </Badge>
                ))
              )}
            </Group>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" className="mb-1">
              Áreas
            </Text>
            <Group gap={4}>
              {(asignaciones.data?.areas ?? []).length === 0 ? (
                <Text size="sm" c="dimmed">
                  Sin áreas asignadas.
                </Text>
              ) : (
                asignaciones.data!.areas.map((a) => (
                  <Badge key={a} size="sm" color="slate">
                    {a}
                  </Badge>
                ))
              )}
            </Group>
          </div>
        </Stack>
      </Paper>
    </Stack>
  );
}

/** Placeholder de la pestaña Perfil mientras llega el dato (mismo layout de 3 cards). */
function TabPerfilSkeleton() {
  return (
    <Stack gap="md">
      <Paper padding="lg">
        <Group gap="md" wrap="nowrap">
          <Skeleton height={56} width={56} />
          <div className="min-w-0 flex-1">
            <Skeleton height={16} width="45%" />
            <Skeleton height={12} width="60%" className="mt-2" />
            <Skeleton height={18} width="30%" className="mt-2 !rounded-full" />
          </div>
        </Group>
      </Paper>
      <Paper padding="lg">
        <Stack gap="sm">
          <Skeleton height={14} width="35%" />
          <Skeleton height={38} />
          <Skeleton height={38} />
        </Stack>
      </Paper>
      <Paper padding="lg">
        <Stack gap="sm">
          <Skeleton height={14} width="30%" />
          <Skeleton height={24} width="70%" />
          <Skeleton height={24} width="55%" />
        </Stack>
      </Paper>
    </Stack>
  );
}

/**
 * La contraseña SOLO se cambia mediante el enlace de restablecimiento al
 * correo (decisión del negocio · demuestra control del buzón). Este bloque
 * dispara ese enlace para el correo de la sesión.
 */
function CardRestablecerPassword() {
  const email = useAuthStore((s) => s.user?.email);
  const [enviado, setEnviado] = useState(false);

  const solicitar = useMutation({
    mutationFn: () => forgotPassword(email ?? ""),
    onSuccess: () => setEnviado(true),
    onError: () => setEnviado(true), // respuesta neutra · igual que /recuperar-contrasena
  });

  return (
    <Paper padding="lg">
      <Stack gap="sm">
        <div>
          <Text fw={600} size="sm">
            Contraseña
          </Text>
          <Text size="xs" c="dimmed">
            Por seguridad, la contraseña solo se cambia mediante un enlace de
            restablecimiento enviado a tu correo.
          </Text>
        </div>
        {enviado ? (
          <Alert variant="success" icon={<IconMailForward size={18} />}>
            Te enviamos el enlace de restablecimiento a <b>{email}</b> · revisa
            tu bandeja (y el spam). El enlace tiene vigencia limitada.
          </Alert>
        ) : (
          <Group>
            <Button
              variant="default"
              leftSection={<IconMailForward size={16} stroke={1.7} />}
              loading={solicitar.isPending}
              onClick={() => solicitar.mutate()}
            >
              Enviarme el enlace de restablecimiento
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
